import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  injectBxIcons,
  barChart05Icon,
  clockIcon,
  dataflow03Icon,
  flashIcon,
  signal01Icon,
  speedometer03Icon,
  wifiIcon,
} from '@beyonnex/bx-angular-ui';
import { formatDateTime } from '../../../models/core.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { CurrentStatePanelComponent } from '../current-state-panel/current-state-panel.component';
import { CurrentStateDataPoint } from '../current-state-panel/current-state-panel.models';
import { DeviceDetailsStore } from '../device-details.store';

@Component({
  selector: 'bxi-pulse-gas-meter-current-state',
  imports: [CurrentStatePanelComponent],
  templateUrl: './pulse-gas-meter-current-state.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PulseGasMeterCurrentStateComponent {
  private readonly store = inject(DeviceDetailsStore);
  private readonly commonTranslate = inject(CommonTranslateService);

  protected readonly device = toSignal(this.store.pulseGasMeter$);
  protected readonly gatewayLink = toSignal(this.store.gatewayLink$);
  protected readonly data = computed<CurrentStateDataPoint[]>(() => {
    const device = this.device();
    const grid = device?.features?.grid?.properties;
    const unit = device?.getMeterUnit();
    const absoluteVolume = device?.getAbsoluteVolume();

    return [
      {
        label: this.commonTranslate.translateThingProperty('features.gasVolume.properties.status.absoluteVolume'),
        // Raw when the meter info names no unit: the reading still means something without one.
        value: unit ? this.commonTranslate.formatUnit(unit, absoluteVolume) : absoluteVolume,
        icon: 'speedometer-03',
        iconStyle: 'text-utility-success-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.battery.properties.status.voltage'),
        value: this.commonTranslate.formatVolts(device?.features?.battery?.properties?.status?.voltage?.value),
        icon: 'flash',
        iconStyle: 'text-fg-warning-primary',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.rssi'),
        value: grid?.rssi,
        icon: 'bar-chart-05',
        iconStyle: 'text-teal-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.snr'),
        value: grid?.snr,
        icon: 'wifi',
        iconStyle: 'text-utility-purple-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.dataRate'),
        value: grid?.dataRate,
        icon: 'dataflow-03',
        iconStyle: 'text-utility-blue-light-600',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.gateway'),
        value: grid?.gateway,
        icon: 'signal-01',
        iconStyle: 'text-utility-gray-blue-500',
        ...this.gatewayLink(),
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.receiveTime'),
        value: formatDateTime(grid?.receiveTime),
        icon: 'clock',
        iconStyle: 'text-utility-blue-light-600',
      },
    ];
  });

  constructor() {
    injectBxIcons([speedometer03Icon, flashIcon, barChart05Icon, wifiIcon, dataflow03Icon, signal01Icon, clockIcon]);
  }
}
